"use client";

import { MODERATOR_COMMUNITIES_API } from "@/lib/apis";
import {
	TGetModeratorCommunitiesDTO,
	TModeratorChannel,
	TModeratorCommunity,
} from "@/types/community";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";

interface ModeratorContextProps {
	communityId: string | undefined;
	channelId: string | undefined;
	isFetchingCommunities: boolean;
	channels: TModeratorChannel[];
	communities: TModeratorCommunity[];
}

const ModeratorContext = createContext<ModeratorContextProps | undefined>(undefined);

export const ModeratorProvider = ({ children }: { children: ReactNode }) => {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const [channels, setChannels] = useState<TModeratorChannel[]>([]);
	const [communities, setCommunities] = useState<TModeratorCommunity[]>([]);
	const [isFetchingCommunities, setIsFetchingCommunities] = useState<boolean>(false);

	const communityId = searchParams.get("community") || undefined;
	const channelId = searchParams.get("channel") || undefined;

	const fetchCommunities = useCallback(async () => {
		try {
			setIsFetchingCommunities(true);

			const communitiesResponse = await fetch(MODERATOR_COMMUNITIES_API);

			const { data } = (await communitiesResponse.json()) as
				| TGetModeratorCommunitiesDTO
				| { data: undefined };

			const formattedCommunities =
				data?.map((community) => ({
					...community,
					channels: community.channels.map((channel) => ({
						id: channel.id,
						type: null,
						name: channel.name,
						slug: channel.slug,
					})),
				})) ?? [];

			setCommunities(formattedCommunities);

			if (!communityId && formattedCommunities.length > 0) {
				const defaultCommunity = formattedCommunities[0];

				const params = new URLSearchParams({
					community: defaultCommunity.id,
				});

				router.push(`${pathname}?${params.toString()}`);

				return;
			}

			const selectedCommunity = formattedCommunities.find(({ id }) => id === communityId);

			if (selectedCommunity) {
				const params = new URLSearchParams({
					community: selectedCommunity.id,
				});

				router.push(`${pathname}?${params.toString()}`);
			}
		} catch (error) {
			console.log("Error occured while fetching communities");
		} finally {
			setIsFetchingCommunities(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		fetchCommunities();
	}, [fetchCommunities]);

	// Sets channels for a selected community
	useEffect(() => {
		if (!communities) {
			return;
		}

		let selectedCommunity = communities.find((community) => community.id === communityId);

		if (!selectedCommunity) {
			selectedCommunity = communities[0];
		}

		setChannels(selectedCommunity?.channels || []);
	}, [communities, communityId]);

	return (
		<ModeratorContext.Provider
			value={{ communityId, channelId, isFetchingCommunities, channels, communities }}
		>
			{children}
		</ModeratorContext.Provider>
	);
};

export const useModeratorContext = () => {
	const context = useContext(ModeratorContext);

	if (!context) {
		throw new Error("useModeratorContext must be used within a ModeratorProvider");
	}

	return context;
};
